import React, { useEffect, useMemo, useState } from "react";
import { accessApi } from "../../services/accessApi";

function Block({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-sm">
      <h2 className="text-base font-semibold text-black dark:text-white mb-3">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    green:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900",
    blue:
      "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900",
    gray:
      "bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700",
    red:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900",
  };

  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-sm">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-black dark:text-white">{value}</div>
    </div>
  );
}

export default function ITMyAccessDebugPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await accessApi.getMyAccess();
      setData(res?.data?.data || null);
    } catch (err) {
      console.error("ITMyAccessDebugPage error:", err);
      setError(err?.response?.data?.message || err.message || "Failed to load access debug.");
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    return {
      roles: data?.hr_roles?.length || 0,
      policies: data?.policies?.length || 0,
      permissions: data?.permissions?.length || 0,
      scopes: data?.scopes?.length || 0,
      modules: data?.visible_modules?.length || 0,
    };
  }, [data]);

  if (loading) return <div className="p-6 text-black dark:text-white">Loading access debug...</div>;
  if (error) return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">My Access Debug</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Effective access for the currently logged user.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
          >
            Refresh
          </button>
          <button
            onClick={() => setShowRaw((v) => !v)}
            className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
          >
            {showRaw ? "Hide raw" : "Show raw"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard label="Roles" value={stats.roles} />
        <StatCard label="Policies" value={stats.policies} />
        <StatCard label="Permissions" value={stats.permissions} />
        <StatCard label="Scopes" value={stats.scopes} />
        <StatCard label="Visible modules" value={stats.modules} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Block title="User">
          <div className="space-y-2 text-sm text-black dark:text-white">
            <div><strong>ID:</strong> {data?.user?.id || "-"}</div>
            <div><strong>Name:</strong> {data?.user?.display_name || "-"}</div>
            <div><strong>Email:</strong> {data?.user?.email || "-"}</div>
            <div><strong>UID:</strong> {data?.user?.uid || "-"}</div>
          </div>
        </Block>

        <Block title="Linked Person">
          <div className="space-y-2 text-sm text-black dark:text-white">
            <div><strong>Person:</strong> {data?.person?.full_name || "-"}</div>
            <div><strong>Position:</strong> {data?.person?.position_title || "-"}</div>
            <div><strong>Department:</strong> {data?.person?.department_name || "-"}</div>
            <div><strong>Team:</strong> {data?.person?.team_name || "-"}</div>
            <div><strong>Status:</strong> {data?.person?.status || "-"}</div>
          </div>
        </Block>
      </div>

      <Block title="Roles">
        <div className="flex flex-wrap gap-2">
          {(data?.hr_roles || []).map((r) => (
            <Badge key={`${r.role_key}-${r.source}`} tone="blue">
              {r.role_key} · {r.source}
            </Badge>
          ))}
          {!data?.hr_roles?.length && <Badge tone="red">No roles</Badge>}
        </div>
      </Block>

      <Block title="Policies">
        <div className="space-y-2">
          {(data?.policies || []).map((p) => (
            <div key={p.code} className="text-sm text-black dark:text-white">
              <strong>{p.code}</strong> — {p.name}
              <span className="text-gray-500 dark:text-gray-400"> ({p.source})</span>
            </div>
          ))}
          {!data?.policies?.length && <div className="text-sm text-gray-500 dark:text-gray-400">No policies.</div>}
        </div>
      </Block>

      <Block title="Permissions by Module">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {Object.entries(
            (data?.permissions || []).reduce((acc, perm) => {
              if (!acc[perm.module_code]) acc[perm.module_code] = [];
              acc[perm.module_code].push(perm);
              return acc;
            }, {})
          ).map(([moduleCode, perms]) => (
            <div
              key={moduleCode}
              className="rounded-xl border border-gray-200 dark:border-neutral-700 p-3"
            >
              <div className="font-semibold text-black dark:text-white mb-2">{moduleCode}</div>
              <div className="flex flex-wrap gap-1.5">
                {perms.map((perm) => (
                  <Badge key={perm.code}>{perm.code}</Badge>
                ))}
              </div>
            </div>
          ))}

          {!data?.permissions?.length && (
            <div className="text-sm text-gray-500 dark:text-gray-400">No permissions.</div>
          )}
        </div>
      </Block>

      <Block title="Scopes">
        <div className="space-y-2">
          {(data?.scopes || []).map((s) => (
            <div key={`${s.module_code}-${s.scope}`} className="text-sm text-black dark:text-white">
              <strong>{s.module_code}</strong> → {s.scope}
              <span className="text-gray-500 dark:text-gray-400"> ({s.source_type}: {s.source_key})</span>
            </div>
          ))}
          {!data?.scopes?.length && <div className="text-sm text-gray-500 dark:text-gray-400">No scopes.</div>}
        </div>
      </Block>

      <Block title="Visible Modules">
        <div className="flex flex-wrap gap-2">
          {(data?.visible_modules || []).map((m) => (
            <Badge key={m}>{m}</Badge>
          ))}
          {!data?.visible_modules?.length && <Badge tone="red">No visible modules</Badge>}
        </div>
      </Block>

      {showRaw && (
        <Block title="Raw Response">
          <pre className="text-xs whitespace-pre-wrap text-black dark:text-white overflow-x-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </Block>
      )}
    </div>
  );
}